import styled from '@emotion/styled';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useMountEffect} from 'webapp-lib/utils';

import {
  equalsToGeneratedState,
  OAUTH_RESPONSE,
  OAuthMessage,
  queryToObject,
} from './OAuth2.utils';

const isWindow = (opener: Window | null): opener is Window =>
  opener !== null && opener !== undefined;

const postMessage = (opener: Window, message: OAuthMessage) =>
  opener.postMessage(message);

/**
 * This component serves as a view on OAuth2 redirect.
 *
 * Parses needed values from OAuth2 response.
 * Verifies that the response corresponds to the OAuth2 request made in `useOAuth2.ts`
 * by comparing state from the response with the state stored in session storage
 *
 * Forward received authorization code via message event to `useOAuth2.ts`
 */
export const OAuth2 = (): React.ReactElement => {
  const {t} = useTranslation('common');
  const [autoClosingPopup, setAutoClosingPopup] = useState(true);

  useMountEffect(() => {
    const parentWindow = window?.opener;
    if (isWindow(parentWindow)) {
      const payload = {
        ...queryToObject(window.location.search.split('?')[1]),
      };

      const stateMatched = equalsToGeneratedState(payload?.state);
      if (stateMatched && 'code' in payload) {
        postMessage(parentWindow, {type: OAUTH_RESPONSE, code: payload.code});
      } else {
        const error = payload?.error;
        const errorMessage = error
          ? decodeURI(error)
          : !stateMatched
            ? t('oauth.error.stateMismatch')
            : t('oauth.error.unknownError');
        postMessage(parentWindow, {type: OAUTH_RESPONSE, error: errorMessage});
      }
    } else {
      setAutoClosingPopup(false);
      throw new Error('No parent window');
    }
  });

  const contentText = autoClosingPopup
    ? t('oauth.autoClosePopup')
    : t('oauth.unableToAutoClosePopup');

  return <StyledOAuthContent>{contentText}</StyledOAuthContent>;
};

const StyledOAuthContent = styled.div`
  margin: var(--gutter-small);
`;
